// ---------------------------------------------------------------------------
// KgGenerationForm — Controlled form body for configuring a Knowledge Graph
// synthetic generation run.
//
// The parent owns the `KgGenerationFormValues` state and submission flow.
// This component renders the inputs and forwards changes via `onChange`.
// Validation is also the parent's responsibility; `isKgFormValid` is exposed
// so the parent can drive the submit button without duplicating rules.
// ---------------------------------------------------------------------------

import {
  Checkbox,
  Field,
  Input,
  Label,
  Radio,
  RadioGroup,
  Select,
  Slider,
  Text,
  Textarea,
} from '@fluentui/react-components';
import {
  ChevronDown20Regular,
  ChevronRight20Regular,
} from '@fluentui/react-icons';
import { useState } from 'react';

import {
  KG_DOMAINS,
  KG_EXPERTISE_BIAS_OPTIONS,
  KG_STYLES,
  KG_TONES,
  type KgDomain,
  type KgExpertiseBias,
  type KgGenerationFormProps,
  type KgGenerationFormValues,
} from './KgGenerationForm.interface.js';
import { useStyles } from './KgGenerationForm.style.js';

export const KG_FORM_DEFAULTS: KgGenerationFormValues = {
  contextMode: 'domain',
  domain: KG_DOMAINS[0]!.value,
  scenario: '',
  totalMessages: 10,
  minUsers: 2,
  maxUsers: 5,
  numTopics: 3,
  numPhases: 2,
  noiseRatio: 0.1,
  tones: ['professional'],
  styles: ['concise'],
  expertiseBias: KG_EXPERTISE_BIAS_OPTIONS[0]!.value,
  useBatchedEpisodes: false,
  useAgent: false,
  agentShare: 0.08,
};

// Each exchange is a request and a reply, so a share of s over n messages is
// about s*n/2 exchanges. Rounded, not floored: flooring discards the half
// exchange and reads a message low (25% of 100 is 12.5, and runs deliver 13).
// Stated concretely because a bare percentage gives no sense of whether the
// agent will show up at all.
export function agentShareHint(share: number, totalMessages: number): string {
  if (share <= 0) {
    return 'Agent stays silent.';
  }
  const exchanges = Math.round((share * totalMessages) / 2);
  const gap = Math.round(2 / share);
  if (exchanges < 1) {
    return `About one exchange per ${gap} messages \u2014 likely none in a ${totalMessages}-message run.`;
  }
  return `About one exchange per ${gap} messages \u2014 roughly ${exchanges} in a ${totalMessages}-message run.`;
}

export function isKgFormValid(values: KgGenerationFormValues): boolean {
  if (
    values.contextMode === 'scenario' &&
    values.scenario.trim().length === 0
  ) {
    return false;
  }
  if (!Number.isFinite(values.totalMessages) || values.totalMessages < 1) {
    return false;
  }
  return true;
}

function toggleInArray(arr: string[], value: string): string[] {
  return arr.includes(value) ? arr.filter((v) => v !== value) : [...arr, value];
}

export function KgGenerationForm({
  values,
  onChange,
  disabled,
}: KgGenerationFormProps) {
  const styles = useStyles();
  const [advancedOpen, setAdvancedOpen] = useState(false);

  const set = <K extends keyof KgGenerationFormValues>(
    key: K,
    value: KgGenerationFormValues[K],
  ) => onChange({ ...values, [key]: value });

  const setNumber = (
    key: 'totalMessages' | 'minUsers' | 'maxUsers' | 'numTopics' | 'numPhases',
    raw: string,
  ) => {
    const parsed = parseInt(raw, 10);
    set(key, Number.isFinite(parsed) ? parsed : 0);
  };

  return (
    <div className={styles.form}>
      {/* Context */}
      <div className={styles.section}>
        <Text className={styles.sectionTitle}>Context</Text>
        <RadioGroup
          value={values.contextMode}
          onChange={(_e, d) =>
            set('contextMode', d.value as 'domain' | 'scenario')
          }
          layout="horizontal"
          disabled={disabled}
        >
          <Radio value="domain" label="Preset domain" />
          <Radio value="scenario" label="Custom scenario" />
        </RadioGroup>
        {values.contextMode === 'domain' ? (
          <Field label="Domain">
            <Select
              value={values.domain}
              onChange={(_e, d) => set('domain', d.value as KgDomain)}
              disabled={disabled}
            >
              {KG_DOMAINS.map(({ value, label }) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </Select>
          </Field>
        ) : (
          <Field
            label="Scenario"
            hint="Describe the context in a few sentences. Projects, phases, and roles are inferred from this."
          >
            <Textarea
              placeholder="e.g. A Microsoft Teams workspace for an engineering team triaging a production incident."
              value={values.scenario}
              onChange={(_e, d) => set('scenario', d.value)}
              rows={4}
              disabled={disabled}
            />
          </Field>
        )}
      </div>

      {/* Scale */}
      <div className={styles.section}>
        <Text className={styles.sectionTitle}>Scale</Text>
        <div className={styles.numberGrid}>
          <Field
            label="Total messages"
            required
            hint={'5\u20131,000 \u00b7 \u22485s/message'}
          >
            <Input
              type="number"
              value={String(values.totalMessages)}
              onChange={(_e, d) => setNumber('totalMessages', d.value)}
              disabled={disabled}
              input={{ className: styles.fullWidthInput }}
              className={styles.fullWidthInput}
            />
          </Field>
          <Field label="Topics" hint={'1\u20135'}>
            <Input
              type="number"
              value={String(values.numTopics)}
              onChange={(_e, d) => setNumber('numTopics', d.value)}
              disabled={disabled}
              input={{ className: styles.fullWidthInput }}
              className={styles.fullWidthInput}
            />
          </Field>
          <Field label="Min users" hint={'2\u201315'}>
            <Input
              type="number"
              value={String(values.minUsers)}
              onChange={(_e, d) => setNumber('minUsers', d.value)}
              disabled={disabled}
              input={{ className: styles.fullWidthInput }}
              className={styles.fullWidthInput}
            />
          </Field>
          <Field label="Max users" hint={'2\u201315'}>
            <Input
              type="number"
              value={String(values.maxUsers)}
              onChange={(_e, d) => setNumber('maxUsers', d.value)}
              disabled={disabled}
              input={{ className: styles.fullWidthInput }}
              className={styles.fullWidthInput}
            />
          </Field>
          <Field label="Phases" hint={'1\u20135'}>
            <Input
              type="number"
              value={String(values.numPhases)}
              onChange={(_e, d) => setNumber('numPhases', d.value)}
              disabled={disabled}
              input={{ className: styles.fullWidthInput }}
              className={styles.fullWidthInput}
            />
          </Field>
        </div>
      </div>

      {/* Advanced (collapsible) */}
      <div className={styles.section}>
        <div
          className={styles.advancedToggle}
          role="button"
          tabIndex={0}
          onClick={() => setAdvancedOpen((o) => !o)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              setAdvancedOpen((o) => !o);
            }
          }}
          aria-expanded={advancedOpen}
        >
          {advancedOpen ? <ChevronDown20Regular /> : <ChevronRight20Regular />}
          <Text className={styles.sectionTitle}>Advanced options</Text>
        </div>

        {advancedOpen && (
          <div className={styles.advancedBody}>
            <Field label="Noise ratio" hint={'0 = clean \u2014 1 = all noise'}>
              <div className={styles.sliderRow}>
                <Slider
                  min={0}
                  max={1}
                  step={0.05}
                  value={values.noiseRatio}
                  onChange={(_e, d) => set('noiseRatio', d.value)}
                  className={styles.slider}
                  disabled={disabled}
                />
                <Text className={styles.sliderValue}>
                  {values.noiseRatio.toFixed(2)}
                </Text>
              </div>
            </Field>

            <div className={styles.checkboxGroup}>
              <Label>Tones</Label>
              <div className={styles.checkboxGrid}>
                {KG_TONES.map((tone) => (
                  <Checkbox
                    key={tone}
                    label={tone}
                    checked={values.tones.includes(tone)}
                    onChange={() =>
                      set('tones', toggleInArray(values.tones, tone))
                    }
                    disabled={disabled}
                  />
                ))}
              </div>
            </div>

            <div className={styles.checkboxGroup}>
              <Label>Writing styles</Label>
              <div className={styles.checkboxGrid}>
                {KG_STYLES.map((s) => (
                  <Checkbox
                    key={s}
                    label={s}
                    checked={values.styles.includes(s)}
                    onChange={() =>
                      set('styles', toggleInArray(values.styles, s))
                    }
                    disabled={disabled}
                  />
                ))}
              </div>
            </div>

            <Field label="Expertise bias">
              <RadioGroup
                value={values.expertiseBias}
                onChange={(_e, d) =>
                  set('expertiseBias', d.value as KgExpertiseBias)
                }
                disabled={disabled}
              >
                {KG_EXPERTISE_BIAS_OPTIONS.map(({ value, label }) => (
                  <Radio key={value} value={value} label={label} />
                ))}
              </RadioGroup>
            </Field>

            <Field
              label="Conversation arcs"
              hint="Plans and writes whole multi-turn exchanges instead of one message at a time. More realistic back-and-forth, but the message count can overshoot slightly."
            >
              <Checkbox
                checked={values.useBatchedEpisodes}
                onChange={(_e, d) =>
                  set('useBatchedEpisodes', d.checked === true)
                }
                disabled={disabled}
                label="Generate multi-turn arcs"
              />
            </Field>

            <Field
              label="Channel agent"
              hint="Adds an AI assistant to the channel. A member asks it for help and it answers from the project's own data."
            >
              <Checkbox
                checked={values.useAgent}
                onChange={(_e, d) => set('useAgent', d.checked === true)}
                disabled={disabled}
                label="Include an assistant in conversations"
              />
            </Field>

            {values.useAgent && (
              <Field
                label="Agent share of messages"
                hint={agentShareHint(values.agentShare, values.totalMessages)}
              >
                <div className={styles.sliderRow}>
                  <Slider
                    min={0}
                    max={0.25}
                    step={0.01}
                    value={values.agentShare}
                    onChange={(_e, d) => set('agentShare', d.value)}
                    className={styles.slider}
                    disabled={disabled}
                  />
                  <Text className={styles.sliderValue}>
                    {`${Math.round(values.agentShare * 100)}%`}
                  </Text>
                </div>
              </Field>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
